import React, { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
// Icons
import UpdateIcon from '@material-ui/icons/Update';
import TodayIcon from '@material-ui/icons/Today';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import DOMPurify from 'dompurify';
import Select from '@material-ui/core/Select';
import { Link, useLocation } from 'react-router-dom';
import InputLabel from '@material-ui/core/InputLabel';
import { MenuItem } from '@material-ui/core';
import InfiniteScroll from 'react-infinite-scroll-component';

const useStyles = makeStyles(() => ({
  scrollButton: {
    width: 'max-content',
    alignSelf: 'center',
    margin: '10px',
  },
  line: {
    borderLeftStyle: 'solid',
    borderLeftWidth: 1,
    display: 'block',
    marginLeft: 16,
    minHeight: 50,
    paddingLeft: 8,
  },
  select: {
    marginTop: '2px',
    maxWidth: '300px',
  },
}));

const notificationsPerPage = 20;

const ActivityFeed = () => {
  const dataProvider = useDataProvider();

  const [notifications, setNotifications] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const categories = [
    ...new Set(eventTypes.map((eventType) => eventType.category)),
  ];

  // Filter related state variables
  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(moment());
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);

  const [hasMore, setHasMore] = useState(true);

  const [page, setPage] = useState(1);
  const location = useLocation();
  const classes = useStyles();

  const getNotifications = () => {
    const filter = {
      start_date: startDate.isValid() ? startDate : null,
      end_date: endDate.isValid() ? endDate : null,
      event_types: selectedEventTypes,
      delivery_method: 'admin',
    };

    dataProvider
      .getList('event-notification', {
        filter,
        pagination: { page, perPage: notificationsPerPage },
        sort: { field: 'created_at', order: 'DESC' },
      })
      .then(({ data, total }) => {
        setNotifications((prevNotifications) => {
          const existingIds = new Set(
            prevNotifications.map((notif) => notif.id),
          );
          const newNotifications = data.filter(
            (notif) => !existingIds.has(notif.id),
          );
          return prevNotifications
            .concat(newNotifications)
            .sort((a, b) => moment(a.created_at).isBefore(b.created_at));
        });

        if (page * notificationsPerPage >= total) {
          setHasMore(false);
        }
      });
  };
  const getEventTypes = () => {
    dataProvider
      .getList('event-type', {
        sort: {},
        pagination: false,
      })
      .then(({ data }) => {
        setEventTypes(data);
      })
      .catch((error) => console.log(error));
  };

  // Get event types and categories only once on initial load
  useEffect(() => {
    getEventTypes();
  }, []);

  useEffect(() => {
    // If filters have been modified, reset pagination
    // Explanation for clearing notifications: we don't want to just concatenate
    // new results as usual since old results may not fit the filter criteria
    setNotifications([]);
    setHasMore(true);

    // In most cases, the 'setPage' will trigger the pagination useEffect and trigger getNotifications
    // However, when the page is '1', setPage(1) will not trigger the effect so call getNotifications manually
    if (page === 1) {
      getNotifications();
    } else {
      setPage(1);
    }
  }, [startDate, endDate, selectedCategories, selectedEventTypes]);

  useEffect(() => {
    getNotifications();
  }, [location, page]);

  // TODO: Get icons from DB
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Schedule':
        return <UpdateIcon fontSize="large" />;
      case 'Booking':
        return <TodayIcon fontSize="large" />;
      case 'Customer':
        return <AccountCircleIcon fontSize="large" />;
      case 'Error':
        return <ErrorIcon fontSize="large" />;
      case 'Booking Intervention Needed':
        return <WarningIcon fontSize="large" />;
      default:
        return <UpdateIcon fontSize="large" />;
    }
  };
  return (
    <div>
      <Grid container direction="column">
        <Grid
          container
          justify="center"
          direction="row"
          spacing={3}
          style={{ margin: '20px 0' }}
        >
          <Grid item>
            <TextField
              label="Start date"
              type="date"
              value={startDate.format('YYYY-MM-DD')}
              onChange={(event) => setStartDate(moment(event.target.value))}
            />
          </Grid>
          <Grid item>
            <TextField
              label="End date"
              type="date"
              value={endDate.format('YYYY-MM-DD')}
              onChange={(event) => setEndDate(moment(event.target.value))}
            />
          </Grid>
          <Grid item>
            <InputLabel shrink>Categories</InputLabel>
            <Select
              className={classes.select}
              displayEmpty
              multiple
              onChange={(event) => {
                setSelectedCategories(event.target.value);
                // Select event types for new category set
                setSelectedEventTypes(
                  eventTypes
                    .filter(({ category }) =>
                      event.target.value.includes(category),
                    )
                    .map((eventType) => eventType.name),
                );
              }}
              value={selectedCategories}
              renderValue={(selected) => {
                // If none are selected, all are selected
                if (selected.length === 0) return <em>All Categories</em>;
                return selected.join(',');
              }}
            >
              {categories.map((category) => (
                <MenuItem value={category}>{category}</MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item>
            <InputLabel shrink>Notifications</InputLabel>
            <Select
              className={classes.select}
              displayEmpty
              multiple
              onChange={(event) => setSelectedEventTypes(event.target.value)}
              value={selectedEventTypes}
              renderValue={(selected) => {
                // If none are selected, all are selected
                if (selected.length === 0) return <em>All Notifications</em>;
                return selected.join(',');
              }}
            >
              {eventTypes.map((eventType) => (
                <MenuItem value={eventType.name}>{eventType.name}</MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
        <InfiniteScroll
          dataLength={notifications.length}
          hasMore={hasMore}
          style={{ overflow: 'hidden' }}
          next={() => setPage((prevPage) => prevPage + 1)}
        >
          {notifications.length === 0 && (
            <Grid xs={9} style={{ margin: '0 auto' }}>
              <Paper style={{ padding: 15 }}>
                <Typography>
                  No Notifications. To subscribe to additional notifications, go
                  to the <Link to="/settings">settings</Link> page and view the
                  &quot;Notifications&quot; panel
                </Typography>
              </Paper>
            </Grid>
          )}
          {notifications.map((notification, index) => {
            const { event_type } = notification.event_record;
            return (
              <Grid
                item
                container
                direction="row"
                justify="flex-start"
                spacing={2}
                key={`notification-${notification.id}`}
              >
                <Grid item xs={2}>
                  <Typography
                    style={{
                      paddingTop: 8,
                      whiteSpace: 'pre-line',
                      textAlign: 'right',
                    }}
                  >
                    {moment
                      .utc(notification.created_at)
                      .local()
                      .format('ddd, MMM Do[\n]h:mma')}
                  </Typography>
                </Grid>
                <Grid item>
                  <Grid item>
                    {getCategoryIcon(event_type.category)}
                    {index < notifications.length - 1 && (
                      <span className={classes.line} />
                    )}
                  </Grid>
                </Grid>
                <Grid item xs={8}>
                  <Paper elevation={3} style={{ padding: 15 }}>
                    <Typography variant="h6">{event_type.name}</Typography>
                    <Typography
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(notification.content),
                      }}
                    />
                  </Paper>
                </Grid>
              </Grid>
            );
          })}
        </InfiniteScroll>
      </Grid>
    </div>
  );
};
export default ActivityFeed;
